import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography,
  Theme,
  Grid,
  TextField,
  Box
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useObserver } from 'mobx-react-lite';
import React, { useState, FC, Fragment } from 'react';
import { isNull, isNullOrUndefined } from 'util';
import { requiredOptionCountForSelectionLevel } from '../../../domain/models/SelectionLevel';
import QuantityField from '../QuantityField/QuantityField';
import { OrderItem as OI, OrderingModifier, OrderModifier } from 'kiolyn-api';
import { LayoutVariant } from '../../Core/CommonTypes';
import EditModifierOptions from './EditModifierOptions';
import { isEmpty } from 'lodash';
import { useOrdering } from '../../../stores/OrderingStore';
import Keyboard, { KeyboardReactInterface } from 'react-simple-keyboard';
import KeyboardField from '../../Core/KeyboardField/KeyboardField';
import { useRef } from 'react';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    height: 'calc(100% - 96px)'
  },
  title: {
    marginRight: 48
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  },
  dialogContent: {},
  mobileDialogContent: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  otherOptions: {
    marginTop: theme.spacing(4)
  },
  kiosk_paperWidth: {
    maxWidth: '1000px',
    width: '800px',
    height: '100%'
  },
  kiosk_container: {
    position: 'relative',
    top: '20%',
    left: '13%',
    height: '50%',
    width: '800px'
  }
}));

interface EditOrderItemProps {
  orderItem: OI;
  allModifiers?: OrderingModifier[];
  onClose?: () => void;
  onSave?: () => void;
  variant?: LayoutVariant;
  showSpecialNote?: boolean;
}

interface EditOrderItemState {
  count: number;
  note: string;
  price_note: number;
  modifiers: OrderModifier[];
}

const EditOrderItem: FC<EditOrderItemProps> = ({
  orderItem,
  allModifiers,
  onClose,
  onSave,
  variant,
  showSpecialNote
}: EditOrderItemProps) => {
  const classes = useStyles();
  const orderingStore = useOrdering();
  const orderingCategories = orderingStore.categories;

  const [state, setState] = useState({
    count: orderItem.count,
    note: orderItem.note,
    price_note: orderItem.price_note || 0,
    modifiers: JSON.parse(JSON.stringify(orderItem.modifiers || []))
  } as EditOrderItemState);
  // Return true if all modifiers is valid
  const validate = () => {
    if (isNullOrUndefined(allModifiers)) {
      return true;
    }
    // Loop through every modifier and make the selected options match
    // with selection_level
    for (const modifier of allModifiers) {
      let minOptionCount = requiredOptionCountForSelectionLevel(
        modifier.selection_level
      );
      const selectedModifier = state.modifiers.find(m => m.id === modifier.id);
      // for sub option exact_one
      if (!isNullOrUndefined(selectedModifier)) {
        for (const option of selectedModifier.options) {
          if (option.required_level === 'exact_one') {
            if (
              isNullOrUndefined(option.suboptions) ||
              option.suboptions?.length === 0
            ) {
              return false;
            }
          }
        }
      }
      if (minOptionCount === 0) {
        continue;
      }
      if (
        isNullOrUndefined(selectedModifier) ||
        selectedModifier.options.length < minOptionCount
      ) {
        return false;
      }
    }
    return true;
  };
  const [valid, setValid] = useState(validate());

  const handleSave = () => {
    orderItem.count = state.count;
    orderItem.note = state.note;
    orderItem.modifiers = state.modifiers;
    orderItem.price_note = state.price_note;
    orderingCategories.forEach(oc =>
      oc.items?.forEach(i => {
        if (i.name === orderItem.name) {
          i.modifiers?.forEach(m => {
            orderItem.modifiers.forEach(im => {
              if (m.id === im.id) {
                im.sameline = m.sameline;
              }
            });
          });
        }
      })
    );

    if (!isNullOrUndefined(onSave)) {
      onSave();
    }
  };

  const handleClose = () => {
    if (!isNullOrUndefined(onClose)) {
      onClose();
    }
  };

  const handleStateChange = (name: string, value: any) => {
    setState({ ...state, [name]: value });
    if (name === 'modifiers') {
      setValid(validate());
    }
  };

  const quantityField = (
    <QuantityField
      key="quantity"
      value={state.count}
      onChange={count => handleStateChange('count', count)}
      disabled={orderItem.IsPromotion}
    />
  );
  const [showKeyboard, setShowKeyboard] = useState(true);
  const keyboardNote = useRef<KeyboardReactInterface>();

  const noteField = showSpecialNote && (
    <>
      <TextField
        key="note"
        onFocus={() => {
          setShowKeyboard(true);
          setTimeout(() => keyboardNote.current?.setInput(state.note), 100);
        }}
        InputLabelProps={{
          shrink: true
        }}
        margin="normal"
        label="Special note"
        fullWidth
        value={state.note}
        onChange={e => handleStateChange('note', e.target.value)}
      />
      {variant === LayoutVariant.kiosk && showKeyboard ? (
        <Keyboard
          keyboardRef={r => (keyboardNote.current = r)}
          onChange={(v: string) => handleStateChange('note', v)}
        />
      ) : null}
    </>
  );

  const notePriceField = showSpecialNote && (
    <>
      <KeyboardField
        margin="normal"
        label="Special note amount"
        placeholder="0.00"
        onFocus={() => setShowKeyboard(false)}
        minimumValue="0"
        value={state.price_note}
        handleValueChange={v => handleStateChange('price_note', v)}
        helperText="Ex: $3.00 extra rice"
        keyboard={{
          enable: variant === LayoutVariant.kiosk && !showKeyboard,
          layout: {
            default: ['1 2 3 4 5 6', '7 8 9 0 {bksp}']
          }
        }}
      />
    </>
  );

  return useObserver(() => {
    if (isNull(orderItem)) {
      return null;
    }
    return (
      <Dialog
        open
        scroll="paper"
        onClose={handleClose}
        fullWidth={variant === LayoutVariant.laptop}
        fullScreen={variant === LayoutVariant.mobile}
        classes={
          variant === LayoutVariant.kiosk
            ? {
                container: classes.kiosk_container,
                paperWidthSm: classes.kiosk_paperWidth
              }
            : variant === LayoutVariant.mobile || isEmpty(allModifiers)
            ? {}
            : { paper: classes.paper }
        }
      >
        <DialogTitle disableTypography>
          <Typography variant="h6" className={classes.title}>
            {orderItem.name}
          </Typography>
          <Typography variant="subtitle1" display="block">
            {orderItem.item_note}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.close}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          classes={{
            root:
              variant === LayoutVariant.mobile
                ? classes.mobileDialogContent
                : classes.dialogContent
          }}
        >
          {variant === LayoutVariant.mobile ? (
            [quantityField, noteField, notePriceField]
          ) : (
            <Grid container direction="column">
              <Grid item>{quantityField}</Grid>
              <Grid item container direction="row" spacing={2}>
                {
                  <Fragment>
                    <Grid item xs={8}>
                      {noteField}
                    </Grid>
                    <Grid item xs={4}>
                      {notePriceField}
                    </Grid>
                  </Fragment>
                }
              </Grid>
            </Grid>
          )}

          {!isEmpty(allModifiers) && (
            <Box>
              <Typography
                variant="h6"
                color="textSecondary"
                className={classes.otherOptions}
              >
                Other options
              </Typography>
              <EditModifierOptions
                allModifiers={allModifiers}
                modifiers={state.modifiers}
                onChange={modifiers =>
                  handleStateChange('modifiers', modifiers)
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions disableSpacing>
          <Button
            disabled={!valid}
            onClick={handleSave}
            color="primary"
            size="large"
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    );
  });
};

export default EditOrderItem;
